import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { MigrationDetailResponse, getMigrationById } from '../api/migrations';

// Shared migration-detail query (FD-127). MigrationLayout owns the canonical
// observer (carrying the FD-126 `?live=1` refetch gate); the three sub-pages
// call this same hook with no interval and read one cache entry
// (queryKey ['migration', id]).
//
// THE DEDUPE IS NOT AUTOMATIC — it is the staleTime below that delivers it.
// This comment used to claim react-query deduped the layout and the child, and
// that claim is what made FD-271 invisible: it reads as verified.
export function useMigrationDetailQuery(
  migrationId: string | undefined,
  options: { refetchInterval?: number | false } = {},
): UseQueryResult<MigrationDetailResponse> {
  return useQuery<MigrationDetailResponse>({
    queryKey: ['migration', migrationId],
    queryFn: () => getMigrationById(migrationId!),
    enabled: Boolean(migrationId),
    // WITHOUT THIS THE PAGE FETCHES TWICE PER VISIT, and this endpoint reads a
    // whole DeviceMigrations partition — so it is a doubled server cost on the
    // most-opened page in the console.
    //
    // MigrationLayout returns a skeleton while loading, so <Outlet/> — and
    // therefore the tab child — does not mount until the first fetch RESOLVES.
    // The child's observer then subscribes to data that is already stale
    // (react-query's default staleTime is 0) and `refetchOnMount` fires a second
    // request. The two observers never overlap in time, so react-query has
    // nothing to dedupe.
    //
    // A short staleTime closes the window. It is not a cache-tuning preference:
    // it is what makes the layout-owns-the-query pattern actually deliver the
    // single fetch it exists for. Tab switches within a visit reuse it too.
    //
    // `refetchInterval` is unaffected — the FD-126 `?live=1` gate still polls on
    // its interval regardless of staleness, so live mode keeps working.
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // Don't retry 404s — the migration genuinely doesn't exist.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
    refetchInterval: options.refetchInterval ?? false,
    refetchIntervalInBackground: false,
  });
}
